import { useCallback, useEffect, useRef, useState } from 'react';
import { magicNumbers } from 'utils';
import { fetchLatestAppVersion } from './useAppVersion';
import * as sw from '../serviceWorkerRegistration';

/**
 * Activates a waiting service worker immediately. User modal choice does not
 * gate this — close / confirm only affects UI; the new build is forced on.
 */
const activateWaitingWorker = (registration) => {
  if (registration?.waiting) {
    sw.applyUpdate(registration);
  }
};

const useServiceWorkerUpdate = () => {
  const refreshingRef = useRef(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);

  const dismissModal = useCallback(() => {
    setPendingUpdate(null);
  }, []);

  /**
   * Force-activate the waiting worker, then dismiss the modal.
   * Reload follows via controllerchange after skipWaiting/clients.claim.
   */
  const forceApplyUpdate = useCallback((registration) => {
    activateWaitingWorker(registration);
    setPendingUpdate(null);
  }, []);

  useEffect(() => {
    // Service worker is temporarily out of the product — no listeners/register.
    if (!sw.SERVICE_WORKER_ENABLED || !('serviceWorker' in navigator)) {
      return undefined;
    }

    const onControllerChange = () => {
      if (refreshingRef.current) return;

      refreshingRef.current = true;
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange
    );

    const announceAndForceUpdate = async (registration) => {
      if (!registration?.waiting) return;

      const latestVersion = await fetchLatestAppVersion();
      setPendingUpdate({
        registration,
        version: latestVersion,
      });
      activateWaitingWorker(registration);
    };

    const syncWaitingWorker = async () => {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await announceAndForceUpdate(registration);
      }
    };

    sw.register({
      onUpdate: (registration) => {
        announceAndForceUpdate(registration);
      },
    });

    syncWaitingWorker();

    const interval = setInterval(
      sw.checkForUpdate,
      magicNumbers.ONE_HOUR_IN_MILLISECONDS
    );

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sw.checkForUpdate().then(syncWaitingWorker);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);

      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange
      );
    };
  }, []);

  return {
    isUpdateModalOpen: Boolean(pendingUpdate),
    updateVersionNumber: pendingUpdate?.version?.number,
    closeUpdateModal: dismissModal,
    applyUpdate: () => forceApplyUpdate(pendingUpdate?.registration),
  };
};

export default useServiceWorkerUpdate;
